import React, { useState, useEffect, useContext } from 'react';
import VariantModal from '../../general/VariantModal';
import SubmitNotificationModal from '../../general/SubmitNotificationModal';
import { getOdooCategories, getOdooCategoriesWeb, createProviderRequest, getOdooBrands } from '../../../hooks/api/odoo';
import { SelectSearch } from '../../general/SelectSearch';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { useProviders } from '../../../hooks/api/clientsProvides';
import { api } from '../../../utils/lib/api';
import { apiRoutes } from '../../../config';

// Componente personalizado de SelectSearch para categorías
const CategorySelectSearch = ({ options, selected, onSelect, placeholder, disabled, emptyMessage }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [containerRef, setContainerRef] = useState(null);

  const showSuggestionsFunction = (action) => {
    if (disabled) return;
    setShowSuggestions(action);
  };

  const onSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    setFilteredSuggestions(
      options
        .filter((suggestion) =>
          suggestion.value.toLowerCase().includes(value.toLowerCase())
        )
        .sort((a, b) => a.value.localeCompare(b.value))
    );

    setShowSuggestions(true);
  };

  const onSuggestionClick = (e) => {
    const selectedSuggestion = options.find(
      (suggestion) => suggestion.id.toString() === e.target.id.toString()
    );
    if (selectedSuggestion) {
      setSearchTerm(selectedSuggestion.value);
      setSelectedId(selectedSuggestion.id);
      setShowSuggestions(false);
      onSelect(selectedSuggestion.id); // Llamar al callback con el ID seleccionado
    }
  };

  useEffect(() => {
    if (options.length > 0) {
      if (selected) {
        const select = options.find(
          (suggestion) => suggestion.id.toString() === selected.toString()
        );
        if (select) {
          setSearchTerm(select.value);
          setSelectedId(select.id);
        }
      }
      setFilteredSuggestions(
        options.sort((a, b) => a.value.localeCompare(b.value))
      );
    }
  }, [options, selected]);

  // Cerrar sugerencias cuando se hace clic fuera del componente
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef && !containerRef.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [containerRef]);

  return (
    <div
      ref={setContainerRef}
      style={{ position: 'relative', width: '100%' }}
    >
      <input
        type='text'
        placeholder={placeholder}
        value={options.length <= 0 ? emptyMessage || "Lista vacía" : searchTerm}
        onChange={onSearchChange}
        onFocus={() => showSuggestionsFunction(true)}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          fontSize: '14px',
          outline: 'none'
        }}
        disabled={disabled}
      />
      {!disabled && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            right: '12px',
            transform: 'translateY(-50%)',
            cursor: 'pointer',
            transition: 'transform 0.3s ease',
            transform: showSuggestions ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%) rotate(0deg)'
          }}
          onClick={() => showSuggestionsFunction(!showSuggestions)}
        >
          ▼
        </div>
      )}
      {showSuggestions && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: '4px',
          maxHeight: '200px',
          overflowY: 'auto',
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          {filteredSuggestions.map((suggestion, index) => (
            <div
              key={index}
              id={suggestion.id}
              onClick={onSuggestionClick}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderBottom: '1px solid #f0f0f0',
                fontSize: '14px'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
            >
              {suggestion.value}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Componente personalizado de SelectSearch para categorías web con búsqueda en tiempo real
const CategoryWebSelectSearch = ({ selected, onSelect, placeholder, disabled, emptyMessage }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [containerRef, setContainerRef] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const showSuggestionsFunction = (action) => {
    if (disabled) return;
    setShowSuggestions(action);
  };

  // Función para buscar categorías web con debounce
  const searchCategoriesWeb = async (searchText) => {
    if (!searchText || searchText.trim() === '') {
      setFilteredSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const entId = "5f1378dcef2f752aeda16673"; // ID de la empresa
      const categories = await getOdooCategoriesWeb(entId, searchText);

      // Validar que categories sea un array o un objeto con la propiedad categories
      if (categories && Array.isArray(categories)) {
        const formattedCategories = categories.map(category => ({
          id: category.id || category.odooId || category._id,
          value: category.name || category.display_name || category.value || category.title || ''
        })).filter(category => category.value && category.value.trim() !== '');

        setFilteredSuggestions(formattedCategories);
      } else if (categories && typeof categories === 'object' && categories.categories) {
        // Si categories es un objeto con la propiedad categories (como en la respuesta real)
        const categoriesArray = categories.categories;
        if (Array.isArray(categoriesArray)) {
          const formattedCategories = categoriesArray.map(category => ({
            id: category.id || category.odooId || category._id,
            value: category.display_name || category.name || category.value || category.title || ''
          })).filter(category => category.value && category.value.trim() !== '');

          setFilteredSuggestions(formattedCategories);
        } else {
          setFilteredSuggestions([]);
        }
      } else if (categories && typeof categories === 'object') {
        // Fallback para otras estructuras de objeto
        const categoriesArray = categories.data || categories.results || [];
        if (Array.isArray(categoriesArray)) {
          const formattedCategories = categoriesArray.map(category => ({
            id: category.id || category.odooId || category._id,
            value: category.name || category.display_name || category.value || category.title || ''
          })).filter(category => category.value && category.value.trim() !== '');

          setFilteredSuggestions(formattedCategories);
        } else {
          setFilteredSuggestions([]);
        }
      } else {
        setFilteredSuggestions([]);
      }
    } catch (error) {
      console.error('Error buscando categorías web:', error);
      setFilteredSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const onSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Limpiar el timeout anterior
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Crear nuevo timeout para debounce de 250ms
    const newTimeout = setTimeout(() => {
      searchCategoriesWeb(value);
    }, 250);

    setSearchTimeout(newTimeout);
    setShowSuggestions(true);
  };

  const onSuggestionClick = (e) => {
    const selectedSuggestion = filteredSuggestions.find(
      (suggestion) => suggestion.id.toString() === e.target.id.toString()
    );
    if (selectedSuggestion) {
      setSearchTerm(selectedSuggestion.value);
      setSelectedId(selectedSuggestion.id);
      setShowSuggestions(false);
      // Pasar tanto el id como el display_name
      onSelect({
        id: selectedSuggestion.id,
        display_name: selectedSuggestion.value
      });
    }
  };

  useEffect(() => {
    if (selected) {
      // Si hay un valor seleccionado, buscar su información
      const searchSelectedCategory = async () => {
        setLoading(true);
        try {
          const entId = "5f1378dcef2f752aeda16673";
          const categories = await getOdooCategoriesWeb(entId, '');

          // Validar que categories sea un array o un objeto con la propiedad categories
          if (categories && Array.isArray(categories)) {
            const selectedCategory = categories.find(cat =>
              (cat.id || cat.odooId || cat._id).toString() === selected.toString()
            );
            if (selectedCategory) {
              setSearchTerm(selectedCategory.name || selectedCategory.display_name || selectedCategory.value || selectedCategory.title || '');
              setSelectedId(selectedCategory.id || selectedCategory.odooId || selectedCategory._id);
            }
          } else if (categories && typeof categories === 'object' && categories.categories) {
            // Si categories es un objeto con la propiedad categories (como en la respuesta real)
            const categoriesArray = categories.categories;
            if (Array.isArray(categoriesArray)) {
              const selectedCategory = categoriesArray.find(cat =>
                (cat.id || cat.odooId || cat._id).toString() === selected.toString()
              );
              if (selectedCategory) {
                setSearchTerm(selectedCategory.display_name || selectedCategory.name || selectedCategory.value || selectedCategory.title || '');
                setSelectedId(selectedCategory.id || selectedCategory.odooId || selectedCategory._id);
              }
            }
          } else if (categories && typeof categories === 'object') {
            // Fallback para otras estructuras de objeto
            const categoriesArray = categories.data || categories.results || [];
            if (Array.isArray(categoriesArray)) {
              const selectedCategory = categoriesArray.find(cat =>
                (cat.id || cat.odooId || cat._id).toString() === selected.toString()
              );
              if (selectedCategory) {
                setSearchTerm(selectedCategory.name || selectedCategory.display_name || selectedCategory.value || selectedCategory.title || '');
                setSelectedId(selectedCategory.id || selectedCategory.odooId || selectedCategory._id);
              }
            }
          }
        } catch (error) {
          console.error('Error cargando categoría seleccionada:', error);
        } finally {
          setLoading(false);
        }
      };
      searchSelectedCategory();
    }
  }, [selected]);

  // Cerrar sugerencias cuando se hace clic fuera del componente
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef && !containerRef.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [containerRef]);

  // Limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Actualizar searchTerm cuando se selecciona una marca
  useEffect(() => {
    if (selected) {
      setSearchTerm(selected.display_name || '');
    } else {
      setSearchTerm('');
    }
  }, [selected]);

  return (
    <div
      ref={setContainerRef}
      style={{ position: 'relative', width: '100%' }}
    >
      <input
        type='text'
        placeholder={placeholder}
        value={searchTerm}
        onChange={onSearchChange}
        onFocus={() => showSuggestionsFunction(true)}
        style={{
          width: '100%',
          padding: '8px 12px',
          paddingRight: loading ? '35px' : '12px',
          border: `1px solid ${loading ? '#007bff' : '#ccc'}`,
          borderRadius: '4px',
          fontSize: '14px',
          outline: 'none',
          transition: 'all 0.3s ease'
        }}
        disabled={disabled}
      />
      {!disabled && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            right: '12px',
            transform: 'translateY(-50%)',
            cursor: loading ? 'default' : 'pointer',
            transition: 'all 0.3s ease',
            transform: showSuggestions && !loading ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%) rotate(0deg)',
            color: loading ? '#007bff' : '#666',
            fontSize: loading ? '16px' : '12px'
          }}
          onClick={() => !loading && showSuggestionsFunction(!showSuggestions)}
        >
          {loading ? '⏳' : '▼'}
        </div>
      )}
      {showSuggestions && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: '4px',
          maxHeight: '200px',
          overflowY: 'auto',
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          {loading ? (
            <div style={{
              padding: '12px',
              textAlign: 'center',
              color: '#666',
              fontSize: '14px'
            }}>
              🔍 Buscando categorías...
            </div>
          ) : filteredSuggestions.length > 0 ? (
            filteredSuggestions.map((suggestion, index) => (
              <div
                key={index}
                id={suggestion.id}
                onClick={onSuggestionClick}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f0f0f0',
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
              >
                {suggestion.value}
              </div>
            ))
          ) : searchTerm && !loading ? (
            <div style={{
              padding: '12px',
              textAlign: 'center',
              color: '#666',
              fontSize: '14px'
            }}>
              No se encontraron categorías para "{searchTerm}"
            </div>
          ) : !searchTerm ? (
            <div style={{
              padding: '12px',
              textAlign: 'center',
              color: '#666',
              fontSize: '14px'
            }}>
              Escribe para buscar categorías web...
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

// Componente para selección múltiple de categorías web
const MultiCategoryWebSelect = ({ selectedCategories, onSelect, onRemove, placeholder, disabled, maxCategories = 5 }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [containerRef, setContainerRef] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const showSuggestionsFunction = (action) => {
    if (disabled || selectedCategories.length >= maxCategories) return;
    setShowSuggestions(action);
  };

  // Función para buscar categorías web con debounce
  const searchCategoriesWeb = async (searchText) => {
    if (!searchText || searchText.trim() === '') {
      setFilteredSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const entId = "5f1378dcef2f752aeda16673";
      const categories = await getOdooCategoriesWeb(entId, searchText);

      if (categories && typeof categories === 'object' && categories.categories) {
        const categoriesArray = categories.categories;
        if (Array.isArray(categoriesArray)) {
          const formattedCategories = categoriesArray
            .map(category => ({
              id: category.id || category.odooId || category._id,
              value: category.display_name || category.name || category.value || category.title || '',
              display_name: category.display_name || category.name || category.value || category.title || ''
            }))
            .filter(category => category.value && category.value.trim() !== '')
            .filter(category => !selectedCategories.some(selected => selected.id === category.id)); // Filtrar ya seleccionadas

          setFilteredSuggestions(formattedCategories);
        } else {
          setFilteredSuggestions([]);
        }
      } else {
        setFilteredSuggestions([]);
      }
    } catch (error) {
      console.error('Error buscando categorías web:', error);
      setFilteredSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const onSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const newTimeout = setTimeout(() => {
      searchCategoriesWeb(value);
    }, 250);

    setSearchTimeout(newTimeout);
    setShowSuggestions(true);
  };

  const onSuggestionClick = (e) => {
    const selectedSuggestion = filteredSuggestions.find(
      (suggestion) => suggestion.id.toString() === e.target.id.toString()
    );
    if (selectedSuggestion && selectedCategories.length < maxCategories) {
      onSelect(selectedSuggestion);
      setSearchTerm("");
      setShowSuggestions(false);
      setFilteredSuggestions([]);
    }
  };

  // Cerrar sugerencias cuando se hace clic fuera del componente
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef && !containerRef.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [containerRef]);

  // Limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  return (
    <div
      ref={setContainerRef}
      style={{ position: 'relative', width: '100%' }}
    >
      {/* Mostrar categorías seleccionadas */}
      {selectedCategories.length > 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: '10px',
          padding: '8px',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          border: '1px solid #dee2e6',
          minHeight: '40px'
        }}>
          {selectedCategories.map((category, index) => (
            <div
              key={category.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                backgroundColor: '#007bff',
                color: 'white',
                borderRadius: '16px',
                fontSize: '12px',
                fontWeight: '500',
                minWidth: '150px',
                maxWidth: '350px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                flexShrink: '0'
              }}
            >
              <span style={{
                flex: '1',
                minWidth: '0',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '12px',
                fontWeight: '500',
                color: 'white',
                lineHeight: '1.2',
                display: 'block',
                paddingRight: '4px'
              }}>
                {category.display_name || 'Sin nombre'}
              </span>
              <button
                type="button"
                onClick={() => onRemove(category.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  padding: '0',
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input de búsqueda */}
      <input
        type='text'
        placeholder={selectedCategories.length >= maxCategories
          ? `Máximo ${maxCategories} categorías alcanzado`
          : placeholder}
        value={searchTerm}
        onChange={onSearchChange}
        onFocus={() => showSuggestionsFunction(true)}
        style={{
          width: '100%',
          padding: '8px 12px',
          paddingRight: loading ? '35px' : '12px',
          border: `1px solid ${loading ? '#007bff' : '#ccc'}`,
          borderRadius: '4px',
          fontSize: '14px',
          outline: 'none',
          transition: 'all 0.3s ease',
          opacity: selectedCategories.length >= maxCategories ? 0.6 : 1
        }}
        disabled={disabled || selectedCategories.length >= maxCategories}
      />

      {!disabled && selectedCategories.length < maxCategories && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            right: '12px',
            transform: 'translateY(-50%)',
            cursor: loading ? 'default' : 'pointer',
            transition: 'all 0.3s ease',
            transform: showSuggestions && !loading ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%) rotate(0deg)',
            color: loading ? '#007bff' : '#666',
            fontSize: loading ? '16px' : '12px'
          }}
          onClick={() => !loading && showSuggestionsFunction(!showSuggestions)}
        >
          {loading ? '⏳' : '▼'}
        </div>
      )}

      {/* Dropdown de sugerencias */}
      {showSuggestions && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: '4px',
          maxHeight: '200px',
          overflowY: 'auto',
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          {loading ? (
            <div style={{
              padding: '12px',
              textAlign: 'center',
              color: '#666',
              fontSize: '14px'
            }}>
              🔍 Buscando categorías...
            </div>
          ) : filteredSuggestions.length > 0 ? (
            filteredSuggestions.map((suggestion, index) => (
              <div
                key={index}
                id={suggestion.id}
                onClick={onSuggestionClick}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f0f0f0',
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
              >
                {suggestion.value}
              </div>
            ))
          ) : searchTerm && !loading ? (
            <div style={{
              padding: '12px',
              textAlign: 'center',
              color: '#666',
              fontSize: '14px'
            }}>
              No se encontraron categorías para "{searchTerm}"
            </div>
          ) : !searchTerm ? (
            <div style={{
              padding: '12px',
              textAlign: 'center',
              color: '#666',
              fontSize: '14px'
            }}>
              Escribe para buscar categorías web... ({selectedCategories.length}/{maxCategories})
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

const BrandSelectSearch = ({ selected, onSelect, placeholder, disabled, emptyMessage }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [containerRef, setContainerRef] = useState(null);

  const showSuggestionsFunction = (action) => {
    setShowSuggestions(action);
  };

  const searchBrands = async (searchText) => {
    if (!searchText || searchText.trim() === '') {
      setFilteredSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const entId = "5f1378dcef2f752aeda16673";
      const brands = await getOdooBrands(entId, searchText);

      if (brands && typeof brands === 'object' && brands.brands) {
        const brandsArray = brands.brands;

        if (Array.isArray(brandsArray)) {
          const formattedBrands = brandsArray
            .map((brand, index) => ({
              id: index + 1, // Usar índice como ID ya que no hay ID en la respuesta
              value: brand || ''
            }))
            .filter(brand => brand.value && brand.value.trim() !== '');

          setFilteredSuggestions(formattedBrands);
        } else {
          setFilteredSuggestions([]);
        }
      } else {
        setFilteredSuggestions([]);
      }
    } catch (error) {
      console.error('Error buscando marcas:', error);
      setFilteredSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const onSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const newTimeout = setTimeout(() => {
      searchBrands(value);
    }, 250);

    setSearchTimeout(newTimeout);
    setShowSuggestions(true);
  };

  const onSuggestionClick = (e) => {
    const selectedSuggestion = filteredSuggestions.find(
      (suggestion) => suggestion.id.toString() === e.target.id.toString()
    );
    if (selectedSuggestion) {
      setSearchTerm(selectedSuggestion.value);
      setShowSuggestions(false);
      setFilteredSuggestions([]);
      // Pasar tanto el id como el display_name
      onSelect({
        id: selectedSuggestion.id,
        display_name: selectedSuggestion.value
      });
    }
  };

  // Cerrar sugerencias cuando se hace clic fuera del componente
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef && !containerRef.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [containerRef]);

  // Limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Actualizar searchTerm cuando se selecciona una marca
  useEffect(() => {
    if (selected) {
      setSearchTerm(selected.display_name || '');
    } else {
      setSearchTerm('');
    }
  }, [selected]);

  return (
    <div
      ref={setContainerRef}
      style={{ position: 'relative', width: '100%' }}
    >
      <input
        type='text'
        placeholder={placeholder}
        value={searchTerm}
        onChange={onSearchChange}
        onFocus={() => showSuggestionsFunction(true)}
        style={{
          width: '100%',
          padding: '8px 12px',
          paddingRight: loading ? '35px' : '12px',
          border: `1px solid ${loading ? '#007bff' : '#ccc'}`,
          borderRadius: '4px',
          fontSize: '14px',
          outline: 'none',
          transition: 'all 0.3s ease'
        }}
        disabled={disabled}
      />
      {!disabled && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            right: '12px',
            transform: 'translateY(-50%)',
            cursor: loading ? 'default' : 'pointer',
            transition: 'all 0.3s ease',
            transform: showSuggestions && !loading ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%) rotate(0deg)',
            color: loading ? '#007bff' : '#666',
            fontSize: loading ? '16px' : '12px'
          }}
          onClick={() => !loading && showSuggestionsFunction(!showSuggestions)}
        >
          {loading ? '⏳' : '▼'}
        </div>
      )}

      {/* Dropdown de sugerencias */}
      {showSuggestions && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: '4px',
          maxHeight: '200px',
          overflowY: 'auto',
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          {loading ? (
            <div style={{
              padding: '12px',
              textAlign: 'center',
              color: '#666',
              fontSize: '14px'
            }}>
              Buscando marcas...
            </div>
          ) : filteredSuggestions.length > 0 ? (
            filteredSuggestions.map((suggestion) => (
              <div
                key={suggestion.id}
                id={suggestion.id}
                onClick={onSuggestionClick}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f0f0f0',
                  fontSize: '14px',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                }}
              >
                {suggestion.value}
              </div>
            ))
          ) : !searchTerm ? (
            <div style={{
              padding: '12px',
              textAlign: 'center',
              color: '#666',
              fontSize: '14px'
            }}>
              Escribe para buscar marcas...
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

const ProductManagementClient = () => {
  const globalContext = useContext(GlobalContext);

  // Usar el hook de proveedores en lugar de la lógica antigua
  const { providersDashboardData, loading: loadingProvider } = useProviders();

  // Obtener datos del proveedor desde el contexto global
  const providerData = providersDashboardData ? {
    ownerId: providersDashboardData.ownerId || 0,
    ownerName: providersDashboardData.ownerName || ""
  } : null;

  const [productData, setProductData] = useState({
    titulo: '',
    marca: '',
    descripcion: '',
    comp: '',
    ancho: '',
    largo: '',
    alto: '',
    anchoPaq: '',
    largoPaq: '',
    altoPaq: '',
    categoria: '',
    categoriaWeb: '',
    categoriaWebName: '', // Agregar campo para el nombre de la categoría web
    unidad: '',
    pais: '',
    bajoPedido: 'order',
    tieneVencimiento: false
  });

  // Estado para múltiples categorías web seleccionadas
  const [selectedWebCategories, setSelectedWebCategories] = useState([]);

  // Estado para la marca seleccionada
  const [selectedBrand, setSelectedBrand] = useState(null);

  const [attributes, setAttributes] = useState([
    { color: '', talla: '' }
  ]);

  const [imagePreview, setImagePreview] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [showAdditionalImages, setShowAdditionalImages] = useState(false);

  // Estado para el modal de variantes
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [modalVariants, setModalVariants] = useState([]); // Nuevo estado para las variantes del modal
  const [variantData, setVariantData] = useState({
    color: '',
    talla: '',
    peso: '',
    volumen: '',
    sku: ''
  });
  const [variantImagePreview, setVariantImagePreview] = useState(null);

  // Estado para las categorías de Odoo
  const [odooCategories, setOdooCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Datos de países
  const countriesData = [
    { id: 57, value: "Alemania" },
    { id: 13, value: "Australia" },
    { id: 31, value: "Brasil" },
    { id: 38, value: "Canadá" },
    { id: 48, value: "China" },
    { id: 121, value: "Corea del Sur" },
    { id: 68, value: "España" },
    { id: 233, value: "Estados Unidos" },
    { id: 75, value: "Francia" },
    { id: 104, value: "India" },
    { id: 100, value: "Indonesia" },
    { id: 109, value: "Italia" },
    { id: 113, value: "Japón" },
    { id: 156, value: "México" },
    { id: 231, value: "Reino Unido" },
    { id: 43, value: "Suiza" },
    { id: 227, value: "Taiwán" },
    { id: 224, value: "Turquía" },
    { id: 238, value: "Venezuela" }
  ];

  // NUEVO: Estado para producto único
  const [isUniqueProduct, setIsUniqueProduct] = useState(false);
  // NUEVO: Estado para datos de la variante única
  const [uniqueVariant, setUniqueVariant] = useState({
    sku: '',
    peso: '',
    volumen: '',
    barcode: '',
    image: null,
    additionalImages: []
  });

  // Estado para controlar si se está eliminando una variante
  const [isDeletingVariant, setIsDeletingVariant] = useState(false);

  const [errors, setErrors] = useState({});

  // Estado para controlar las keys de los componentes de selección
  const [formKey, setFormKey] = useState(0);

  // Estado para la notificación modal
  const [notification, setNotification] = useState({ open: false, type: 'success', title: '', message: '' });

  // Cargar categorías de Odoo al montar el componente
  useEffect(() => {
    const loadCategories = async () => {
      setLoadingCategories(true);
      try {
        // Asumiendo que tienes el entId disponible, si no lo tienes, necesitarás obtenerlo del contexto o props
        const entId = "5f1378dcef2f752aeda16673"; // Reemplaza con el entId real
        const categories = await getOdooCategories(entId, 500);
        if (categories && categories.cat) {
          setOdooCategories(categories.cat);
        }
      } catch (error) {
        console.error('Error cargando categorías:', error);
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
  }, []);




  // Limpiar variantes inválidas automáticamente
  useEffect(() => {
    if (modalVariants.length > 0) {
      const validVariants = modalVariants.filter(variant =>
        variant && (variant.sku || (variant.odooAttributes && variant.odooAttributes.length > 0))
      );

      if (validVariants.length !== modalVariants.length) {
        console.log('Limpiando variantes inválidas automáticamente');
        setModalVariants(validVariants);
      }
    }
  }, [modalVariants]);

  // Función para manejar la selección de categoría
  const handleCategorySelect = (categoryId) => {
    handleInputChange('categoria', categoryId);
  };

  // Función para manejar la selección de categoría
  const handleCategoryWebSelect = (categoryData) => {
    // Agregar nueva categoría web
    if (typeof categoryData === 'object' && categoryData.id) {
      const newCategory = {
        id: categoryData.id,
        display_name: categoryData.display_name || categoryData.value || categoryData.name || 'Sin nombre'
      };



      // Verificar que no esté ya seleccionada
      const isAlreadySelected = selectedWebCategories.some(cat => cat.id === newCategory.id);
      if (!isAlreadySelected && selectedWebCategories.length < 5) {
        setSelectedWebCategories(prev => [...prev, newCategory]);
      }
    }
  };

  const handleCategoryWebRemove = (categoryId) => {
    // Remover categoría web
    setSelectedWebCategories(prev => prev.filter(cat => cat.id !== categoryId));
  };

  // Función para manejar la selección de marca
  const handleBrandSelect = (brandData) => {
    if (typeof brandData === 'object' && brandData.id) {
      const newBrand = {
        id: brandData.id,
        display_name: brandData.value || brandData.display_name || 'Sin nombre'
      };

      setSelectedBrand(newBrand);
      // También actualizar el productData para mantener compatibilidad
      setProductData(prev => ({
        ...prev,
        marca: newBrand.display_name
      }));
    }
  };

  // Función para manejar la selección de país
  const handleCountrySelect = (countryId) => {
    handleInputChange('pais', countryId);
  };

  const handleInputChange = (field, value) => {
    setProductData(prev => ({
      ...prev,
      [field]: value
    }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  // Función auxiliar para calcular el volumen con valores actualizados
  const calculateVolumeFromUpdatedData = (updatedField, updatedValue, currentProductData) => {
    const ancho = updatedField === 'anchoPaq'
      ? (updatedValue === '' ? 0 : parseFloat(updatedValue) || 0)
      : (currentProductData.anchoPaq === '' ? 0 : parseFloat(currentProductData.anchoPaq) || 0);

    const largo = updatedField === 'largoPaq'
      ? (updatedValue === '' ? 0 : parseFloat(updatedValue) || 0)
      : (currentProductData.largoPaq === '' ? 0 : parseFloat(currentProductData.largoPaq) || 0);

    const alto = updatedField === 'altoPaq'
      ? (updatedValue === '' ? 0 : parseFloat(updatedValue) || 0)
      : (currentProductData.altoPaq === '' ? 0 : parseFloat(currentProductData.altoPaq) || 0);

    return { ancho, largo, alto };
  };

  const handleAttributeChange = (index, field, value) => {
    const newAttributes = [...attributes];
    newAttributes[index][field] = value;
    setAttributes(newAttributes);
  };

  const addVariant = () => {
    setShowVariantModal(true);
  };

  // Manejar cambios en los datos de la variante del modal
  const handleVariantDataChange = (field, value) => {
    setVariantData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Manejar subida de imagen de variante
  const handleVariantImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setVariantImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Guardar variantes del modal
  const saveVariants = (variants) => {
    // Convertir las variantes del modal al formato esperado por el componente
    const convertedVariants = variants.map(variant => ({
      color: variant.odooAttributes?.find(attr => attr.attributeName.toLowerCase().includes('color'))?.valueName || '',
      talla: variant.odooAttributes?.find(attr => attr.attributeName.toLowerCase().includes('talla'))?.valueName || '',
      peso: variant.peso || '',
      volumen: variant.volumen || '',
      sku: variant.sku || '',
      barcode: variant.barcode || '', // Agregar el barcode
      image: variant.mainImagePreview || variant.image || '', // Preservar imagen existente
      additionalImages: variant.additionalImagesPreview || variant.additionalImages || [], // Preservar imágenes adicionales existentes
      odooAttributes: variant.odooAttributes || []
    }));

    // Reemplazar todas las variantes (no agregar, sino reemplazar)
    setModalVariants(convertedVariants);
    setShowVariantModal(false);
  };

  // Cerrar modal sin guardar
  const closeVariantModal = () => {
    setShowVariantModal(false);
  };

  // Eliminar variante del modal
  const removeModalVariant = (index) => {
    console.log('Eliminando variante en índice:', index);
    console.log('Variantes antes de eliminar:', modalVariants);

    // Marcar que se está eliminando una variante
    setIsDeletingVariant(true);

    setModalVariants(prev => {
      const newVariants = prev.filter((_, i) => i !== index);
      console.log('Variantes después de eliminar:', newVariants);

      // Limpiar variantes inválidas automáticamente
      const validVariants = newVariants.filter(variant =>
        variant && (variant.sku || (variant.odooAttributes && variant.odooAttributes.length > 0))
      );

      if (validVariants.length !== newVariants.length) {
        console.log('Limpiando variantes inválidas:', validVariants);
        // Resetear el estado después de un breve delay
        setTimeout(() => setIsDeletingVariant(false), 100);
        return validVariants;
      }

      // Resetear el estado después de un breve delay
      setTimeout(() => setIsDeletingVariant(false), 100);
      return newVariants;
    });
  };

  // Función para validar imagen
  const validateImage = (file) => {
    return new Promise((resolve, reject) => {
      if (file.size > 1024 * 1024) {
        reject('La imagen debe tener un tamaño máximo de 1MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        reject('El archivo debe ser una imagen');
        return;
      }
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        if (img.width !== img.height) {
          reject('La imagen debe ser cuadrada (mismo ancho y alto)');
          return;
        }
        if (img.width > 1500 || img.height > 1500) {
          reject('La imagen debe tener un tamaño máximo de 1500x1500 píxeles');
          return;
        }
        resolve(true);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject('Error al cargar la imagen');
      };

      img.src = url;
    });
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        await validateImage(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        alert(`Error: ${error}`);
        if (event.target && event.target.value !== undefined) {
          event.target.value = '';
        }
      }
    }
  };

  const handleAdditionalImagesUpload = async (event) => {
    const files = Array.from(event.target.files);

    if (additionalImages.length + files.length > 5) {
      alert('No puedes cargar más de 5 imágenes adicionales en total');
      if (event.target && event.target.value !== undefined) {
        event.target.value = '';
      }
      return;
    }

    for (const file of files) {
      try {
        await validateImage(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          setAdditionalImages(prev => [...prev, e.target.result]);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        alert(`Error con imagen ${file.name}: ${error}`);
      }
    }

    if (event.target && event.target.value !== undefined) {
      event.target.value = '';
    }
  };

  const removeAdditionalImage = (index) => {
    setAdditionalImages(prev => prev.filter((_, i) => i !== index));
  };

  // Función para convertir imagen a base64
  const convertImageToBase64 = (imageDataUrl) => {
    if (!imageDataUrl) return null;
    // Si ya es base64, retornarlo directamente
    if (imageDataUrl.startsWith('data:image')) {
      // Eliminar el prefijo data:image/... y dejar solo el contenido puro del base64
      return imageDataUrl.replace(/^data:[^;]+;base64,/, "");
    }
    return imageDataUrl;
  };

  // Función para convertir múltiples imágenes a base64
  const convertImagesToBase64 = (images) => {
    if (!images || images.length === 0) return [];
    return images.map(img => convertImageToBase64(img));
  };

  // Función para generar atributos únicos de todas las variantes
  const generateUniqueAttributes = () => {
    console.log('Generando atributos únicos para variantes:', modalVariants);

    // Si no hay variantes, retornar array vacío
    if (!modalVariants || modalVariants.length === 0) {
      console.log('No hay variantes, retornando atributos vacíos');
      return [];
    }

    const allAttributes = new Map();

    modalVariants.forEach((variant, index) => {
      console.log(`Procesando variante ${index}:`, variant);
      if (variant.odooAttributes && variant.odooAttributes.length > 0) {
        variant.odooAttributes.forEach(attr => {
          if (!allAttributes.has(attr.attributeName)) {
            allAttributes.set(attr.attributeName, {
              attributeId: parseInt(attr.attributeId) || 1,
              attributeName: attr.attributeName,
              values: new Map()
            });
          }
          allAttributes.get(attr.attributeName).values.set(attr.valueName, {
            valueId: parseInt(attr.valueId) || 100,
            valueName: attr.valueName
          });
        });
      }
    });

    const attributes = [];

    allAttributes.forEach((attrData, attributeName) => {
      const valueIds = Array.from(attrData.values.values()).map(v => v.valueId);
      const valueNames = Array.from(attrData.values.values()).map(v => v.valueName);

      attributes.push({
        attributeId: attrData.attributeId,
        attributeName: attrData.attributeName,
        valueIds: valueIds,
        valueNames: valueNames
      });
    });

    console.log('Atributos únicos generados:', attributes);
    return attributes;
  };

  // Función para obtener el nombre de la unidad de medida
  const getUnitName = (unitId) => {
    const unitMap = {
      1: "Unidad",
      2: "Kilogramos",
      3: "Litros",
      4: "Metros"
    };
    return unitMap[unitId] || "Unidad";
  };

  // Función para generar attributeValues con datos de Odoo
  const generateAttributeValues = (variant) => {
    if (!variant.odooAttributes || variant.odooAttributes.length === 0) {
      return [
        {
          attributeId: 1,
          attributeName: "Color",
          valueId: 100,
          valueName: "Default"
        },
        {
          attributeId: 2,
          attributeName: "Talla",
          valueId: 200,
          valueName: "Default"
        }
      ];
    }

    return variant.odooAttributes.map(attr => ({
      attributeId: parseInt(attr.attributeId) || 1,
      attributeName: attr.attributeName || "Atributo",
      valueId: parseInt(attr.valueId) || 100,
      valueName: attr.valueName || "Valor"
    }));
  };

  // Función para obtener las categorías web seleccionadas (usando datos ya guardados)
  const getWebCategories = () => {
    if (!selectedWebCategories || selectedWebCategories.length === 0) {
      return [];
    }
    return selectedWebCategories.map(category => ({
      categoryId: parseInt(category.id),
      categoryName: category.display_name
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Evitar envío si se está eliminando una variante
    if (isDeletingVariant) {
      console.log('Evitando envío porque se está eliminando una variante');
      return;
    }

    // Validación de campos obligatorios
    const newErrors = {};
    if (!productData.titulo) newErrors.titulo = "Este campo es obligatorio";
    if (!selectedBrand) newErrors.marca = "Debes seleccionar una marca";
    if (!productData.descripcion) newErrors.descripcion = "Este campo es obligatorio";
    if (!productData.comp) newErrors.comp = "Este campo es obligatorio";
    if (!productData.ancho) newErrors.ancho = "Este campo es obligatorio";
    if (!productData.largo) newErrors.largo = "Este campo es obligatorio";
    if (!productData.alto) newErrors.alto = "Este campo es obligatorio";
    if (!productData.anchoPaq) newErrors.anchoPaq = "Este campo es obligatorio";
    if (!productData.largoPaq) newErrors.largoPaq = "Este campo es obligatorio";
    if (!productData.altoPaq) newErrors.altoPaq = "Este campo es obligatorio";
    if (!productData.categoria) newErrors.categoria = "Este campo es obligatorio";
    if (!productData.unidad) newErrors.unidad = "Este campo es obligatorio";
    if (!productData.pais) newErrors.pais = "Este campo es obligatorio";
    if (!imagePreview) newErrors.imagePreview = "La imagen principal es obligatoria";

    // Validar categorías web (mínimo 1, máximo 5)
    if (!selectedWebCategories || selectedWebCategories.length === 0) {
      newErrors.categoriaWeb = "Debes seleccionar al menos una categoría web";
    } else if (selectedWebCategories.length > 5) {
      newErrors.categoriaWeb = "No puedes seleccionar más de 5 categorías web";
    }

    // Validar imágenes adicionales si el checkbox está activo
    if (showAdditionalImages && additionalImages.length === 0) {
      newErrors.additionalImages = "Debes agregar al menos una imagen adicional cuando el checkbox está activo";
    }

    // Si hay errores, no envíes el formulario
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    // NUEVO: Si es producto único, armar el payload especial
    if (isUniqueProduct) {
      // Validar datos mínimos para producto único
      const uniqueVariantErrors = {};

      if (!productData.titulo) {
        alert("Por favor, ingresa el título del producto");
        return;
      }
      if (!uniqueVariant.sku) {
        uniqueVariantErrors.sku = "El SKU es obligatorio para productos únicos";
      }
      if (!uniqueVariant.peso) {
        uniqueVariantErrors.peso = "El peso es obligatorio para productos únicos";
      }
      if (!uniqueVariant.volumen) {
        uniqueVariantErrors.volumen = "El volumen es obligatorio para productos únicos";
      }
      if (!uniqueVariant.barcode) {
        uniqueVariantErrors.barcode = "El código de barras es obligatorio para productos únicos";
      } else if (uniqueVariant.barcode.length < 8) {
        uniqueVariantErrors.barcode = "El código de barras debe tener al menos 8 dígitos";
      } else if (!/^\d+$/.test(uniqueVariant.barcode)) {
        uniqueVariantErrors.barcode = "El código de barras solo puede contener números";
      }

      // Si hay errores en la variante única, mostrarlos y no continuar
      if (Object.keys(uniqueVariantErrors).length > 0) {
        setErrors(prev => ({ ...prev, uniqueVariant: uniqueVariantErrors }));
        return;
      }

      // Obtener nombres descriptivos
      const selectedCategory = odooCategories.find(cat => cat.odooId === parseInt(productData.categoria));
      const selectedCountry = countriesData.find(country => country.id === parseInt(productData.pais));
      const selectedUnit = getUnitName(parseInt(productData.unidad));

      // Obtener categorías web
      const webCategories = getWebCategories();

      const payload = {
        ownerId: providerData?.ownerId || 0,
        ownerName: providerData?.ownerName || "",
        productName: productData.titulo || "",
        brand: selectedBrand?.display_name || "",
        description: productData.descripcion || "",
        mainImage: convertImageToBase64(imagePreview),
        additionalImages: convertImagesToBase64(additionalImages),
        categoryId: parseInt(productData.categoria) || 0,
        categoryName: selectedCategory?.display_name || "",
        webCategories: webCategories, // Agregar categorías web
        composition: productData.comp || "",
        unitOfMeasure: parseInt(productData.unidad) || 1,
        unitOfMeasureName: selectedUnit,
        originOfGoodsId: parseInt(productData.pais) || 0,
        originOfGoodsName: selectedCountry?.value || "",

        invoicePolicy: productData.bajoPedido || 'order',
        hasExpirationDate: productData.tieneVencimiento || false,

        length: parseFloat(productData.largo) || 0,
        width: parseFloat(productData.ancho) || 0,
        height: parseFloat(productData.alto) || 0,
        packageLength: parseFloat(productData.largoPaq) || 0,
        packageWidth: parseFloat(productData.anchoPaq) || 0,
        packageHeight: parseFloat(productData.altoPaq) || 0,
        attributes: [], // Sin atributos
        variantAttributeMapping: [
          {
            internalReference: uniqueVariant.sku,
            barcode: uniqueVariant.barcode || '',
            weight: parseFloat(uniqueVariant.peso) || 0,
            volume: parseFloat(uniqueVariant.volumen) || 0,
            image: convertImageToBase64(uniqueVariant.image),
            additionalImages: convertImagesToBase64(uniqueVariant.additionalImages || []),
            attributeValues: [] // Sin atributos
          }
        ],
        isSimple: isUniqueProduct // Dinámico
      };
      // Validar que haya datos mínimos
      if (!payload.productName) {
        alert("Por favor, ingresa el título del producto");
        return;
      }

      if (payload.variantAttributeMapping.length === 0) {
        alert("Por favor, agrega al menos una variante");
        return;
      }

      console.log('Payload generado:', payload);
      console.log('Datos del producto:', productData);
      console.log('Variantes del modal:', modalVariants);
      console.log('Datos del proveedor:', providerData);

      // Guardar referencia al botón antes del envío
      const submitButton = e.target.querySelector('button[type="submit"]');
      const originalText = submitButton?.textContent || ' Enviar Producto';

      try {
        // Mostrar indicador de carga
        setNotification({ open: true, type: 'loading', title: 'Enviando solicitud...', message: 'Por favor espere mientras procesamos su producto.' });
        if (submitButton) {
          submitButton.disabled = true;
        }

        // Enviar usando el endpoint createProviderRequest
        const entId = "5f1378dcef2f752aeda16673"; // ID de la empresa
        const response = await createProviderRequest(entId, payload);

        if (response) {
          setNotification({ open: true, type: 'success', title: '¡Producto enviado!', message: 'Tu solicitud de producto ha sido enviada exitosamente. Será revisada por el equipo a la brevedad.' });
          console.log('Respuesta del servidor:', response);

          // Limpiar el formulario después del envío exitoso
          setProductData({
            titulo: '',
            marca: '',
            descripcion: '',
            comp: '',
            ancho: '',
            largo: '',
            alto: '',
            anchoPaq: '',
            largoPaq: '',
            altoPaq: '',
            categoria: '',
            categoriaWeb: '',
            categoriaWebName: '',
            unidad: '',
            pais: '',
            bajoPedido: 'order',
            tieneVencimiento: false
          });
          setSelectedWebCategories([]); // Limpiar categorías web seleccionadas
          setSelectedBrand(null); // Limpiar marca seleccionada
          setModalVariants([]);
          setImagePreview(null);
          setAdditionalImages([]);
          setShowAdditionalImages(false);
          setIsUniqueProduct(false);
          setUniqueVariant({
            sku: '',
            peso: '',
            volumen: '',
            barcode: '',
            image: null,
            additionalImages: []
          });
          setErrors({});
          setFormKey(prev => prev + 1); // Incrementar la key para forzar re-renderizado
        } else {
          setNotification({ open: true, type: 'error', title: 'Error al enviar', message: 'Ocurrió un problema al enviar el producto. Por favor, intenta de nuevo.' });
        }
      } catch (error) {
        console.error('Error al enviar el producto:', error);
        setNotification({ open: true, type: 'error', title: 'Error al enviar', message: error?.response?.data?.message || 'Ocurrió un error inesperado. Por favor, verifica los datos e intenta de nuevo.' });
      } finally {
        // Restaurar el botón usando la referencia guardada o buscar nuevamente
        try {
          const buttonToRestore = submitButton || document.querySelector('button[type="submit"]');
          if (buttonToRestore) {
            buttonToRestore.textContent = originalText;
            buttonToRestore.disabled = false;
          }
        } catch (buttonError) {
          console.warn('No se pudo restaurar el botón:', buttonError);
        }
      }
      return;
    }

    // Obtener nombres descriptivos
    const selectedCategory = odooCategories.find(cat => cat.odooId === parseInt(productData.categoria));
    const selectedCountry = countriesData.find(country => country.id === parseInt(productData.pais));
    const selectedUnit = getUnitName(parseInt(productData.unidad));

    // Obtener categorías web
    const webCategories = getWebCategories();

    // Generar el payload con la estructura requerida según el esquema de validación
    const payload = {
      // Información del proveedor
      ownerId: providerData?.ownerId || 0,
      ownerName: providerData?.ownerName || "",

      // Información del producto
      productName: productData.titulo || "",
      brand: selectedBrand?.display_name || "",
      description: productData.descripcion || "",
      mainImage: convertImageToBase64(imagePreview),
      additionalImages: convertImagesToBase64(additionalImages),

      // Categoría
      categoryId: parseInt(productData.categoria) || 0,
      categoryName: selectedCategory?.display_name || "",
      webCategories: webCategories, // Agregar categorías web

      // Campos opcionales del template
      composition: productData.comp || "",
      unitOfMeasure: parseInt(productData.unidad) || 1,
      unitOfMeasureName: selectedUnit,
      originOfGoodsId: parseInt(productData.pais) || 0,
      originOfGoodsName: selectedCountry?.value || "",

      invoicePolicy: productData.bajoPedido || 'order',
      hasExpirationDate: productData.tieneVencimiento || false,

      // Dimensiones del template
      length: parseFloat(productData.largo) || 0,
      width: parseFloat(productData.ancho) || 0,
      height: parseFloat(productData.alto) || 0,
      packageLength: parseFloat(productData.largoPaq) || 0,
      packageWidth: parseFloat(productData.anchoPaq) || 0,
      packageHeight: parseFloat(productData.altoPaq) || 0,

      // Atributos con nombres legibles
      attributes: generateUniqueAttributes(),

      // Mapeo de variantes con nombres legibles
      variantAttributeMapping: modalVariants
        .filter(variant => variant && (variant.sku || variant.odooAttributes)) // Filtrar variantes válidas
        .map((variant, index) => {
          console.log(`Mapeando variante ${index}:`, variant);
          return {
            internalReference: variant.sku || `SKU-${Math.random().toString(36).substr(2, 9).toUpperCase()}`,
            barcode: variant.barcode || "",
            weight: parseFloat(variant.peso) || 0,
            volume: parseFloat(variant.volumen) || 0,
            image: convertImageToBase64(variant.image),
            additionalImages: convertImagesToBase64(variant.additionalImages || []),
            attributeValues: generateAttributeValues(variant)
          };
        }),
      isSimple: isUniqueProduct // Dinámico
    };

    console.log("PAYLOAD DEBUG CHECK:", payload); // Debugging payload

    // Validar que haya datos mínimos
    if (!payload.productName) {
      alert("Por favor, ingresa el título del producto");
      return;
    }

    // Validación adicional para asegurar que hay variantes
    console.log('Validando variantes antes del envío:');
    console.log('modalVariants:', modalVariants);
    console.log('payload.variantAttributeMapping:', payload.variantAttributeMapping);

    if (payload.variantAttributeMapping.length === 0) {
      alert("Por favor, agrega al menos una variante antes de enviar el producto");
      return;
    }

    // Validación adicional para verificar que las variantes tienen datos válidos
    const validVariants = payload.variantAttributeMapping.filter(variant =>
      variant.internalReference && variant.internalReference.trim() !== ''
    );

    if (validVariants.length === 0) {
      alert("Por favor, asegúrate de que todas las variantes tengan un SKU válido");
      return;
    }

    console.log('Payload generado:', payload);
    console.log('Datos del producto:', productData);
    console.log('Variantes del modal:', modalVariants);
    console.log('Datos del proveedor:', providerData);

    // Guardar referencia al botón antes del envío
    const submitButton = e.target.querySelector('button[type="submit"]');
    const originalText = submitButton?.textContent || '🚀 Enviar Producto';

    try {
      // Mostrar indicador de carga
      setNotification({ open: true, type: 'loading', title: 'Enviando solicitud...', message: 'Por favor espere mientras procesamos su producto con variantes.' });
      if (submitButton) {
        submitButton.disabled = true;
      }

      // Enviar usando el endpoint createProviderRequest
      const entId = "5f1378dcef2f752aeda16673"; // ID de la empresa
      const response = await createProviderRequest(entId, payload);

      if (response) {
        setNotification({ open: true, type: 'success', title: '¡Producto enviado!', message: 'Tu solicitud de producto con variantes ha sido enviada exitosamente. Será revisada por el equipo a la brevedad.' });
        console.log('Respuesta del servidor:', response);

        // Limpiar el formulario después del envío exitoso
        setProductData({
          titulo: '',
          marca: '',
          descripcion: '',
          comp: '',
          ancho: '',
          largo: '',
          alto: '',
          anchoPaq: '',
          largoPaq: '',
          altoPaq: '',
          categoria: '',
          categoriaWeb: '',
          categoriaWebName: '',
          unidad: '',
          pais: ''
        });
        setSelectedWebCategories([]); // Limpiar categorías web seleccionadas
        setSelectedBrand(null); // Limpiar marca seleccionada
        setModalVariants([]);
        setImagePreview(null);
        setAdditionalImages([]);
        setShowAdditionalImages(false);
        setIsUniqueProduct(false);
        setUniqueVariant({
          sku: '',
          peso: '',
          volumen: '',
          barcode: '',
          image: null,
          additionalImages: []
        });
        setErrors({});
        setFormKey(prev => prev + 1); // Incrementar la key para forzar re-renderizado
      } else {
        setNotification({ open: true, type: 'error', title: 'Error al enviar', message: 'Ocurrió un problema al enviar el producto. Por favor, intenta de nuevo.' });
      }
    } catch (error) {
      console.error('Error al enviar el producto:', error);
      setNotification({ open: true, type: 'error', title: 'Error al enviar', message: error?.response?.data?.message || 'Ocurrió un error inesperado. Por favor, verifica los datos e intenta de nuevo.' });
    } finally {
      // Restaurar el botón usando la referencia guardada o buscar nuevamente
      try {
        const buttonToRestore = submitButton || document.querySelector('button[type="submit"]');
        if (buttonToRestore) {
          buttonToRestore.textContent = originalText;
          buttonToRestore.disabled = false;
        }
      } catch (buttonError) {
        console.warn('No se pudo restaurar el botón:', buttonError);
      }
    }
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      minHeight: '100vh',
      padding: '30px 20px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header Moderno */}
        <div style={{
          background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
          padding: '30px',
          borderRadius: '16px',
          marginBottom: '30px',
          boxShadow: '0 8px 32px rgba(0, 123, 255, 0.3)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: '-50%',
            right: '-50%',
            width: '200%',
            height: '200%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            animation: 'pulse 3s infinite'
          }}></div>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <h2 style={{
                margin: '0',
                fontWeight: '800',
                fontSize: '28px',
                textShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                Creación de Producto
              </h2>
            </div>
            <p style={{
              margin: '0',
              fontSize: '16px',
              opacity: '0.9',
              fontWeight: '300'
            }}>
              Completa la información de tu producto y sus variantes
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
          {/* Formulario Principal */}
          <div style={{ flex: '2', minWidth: '300px' }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              overflow: 'hidden',
              border: '1px solid rgba(0,0,0,0.05)'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                padding: '25px',
                borderBottom: '1px solid #dee2e6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{
                    backgroundColor: '#007bff',
                    color: 'white',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '15px',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    boxShadow: '0 4px 12px rgba(0, 123, 255, 0.3)'
                  }}>
                    📦
                  </span>
                  <h4 style={{ margin: '0', fontWeight: '700', color: '#333', fontSize: '20px' }}>
                    Información del Producto
                  </h4>
                </div>
              </div>

              <div style={{ padding: '20px' }}>
                <form onSubmit={handleSubmit}>
                  {/* Campo Título - 100% de ancho */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', color: '#333', marginBottom: '8px', fontSize: '16px' }}>
                      Título del Producto: <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={productData.titulo}
                      onChange={(e) => handleInputChange('titulo', e.target.value)}
                      placeholder="Ingrese el título del producto"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: `1px solid ${errors.titulo ? '#dc3545' : '#ccc'}`,
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '500',
                        backgroundColor: 'white',
                        transition: 'border-color 0.3s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#0056b3'}
                      onBlur={(e) => e.target.style.borderColor = errors.titulo ? '#dc3545' : '#007bff'}
                    />
                    {errors.titulo && (
                      <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '4px', fontWeight: 'bold' }}>
                        {errors.titulo}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                    {/* Columna Izquierda */}
                    <div style={{ flex: '1', minWidth: '250px' }}>
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                          Marca: <span style={{ color: '#dc3545' }}>*</span>
                        </label>
                        <BrandSelectSearch
                          key={`brand-select-${formKey}`}
                          selected={selectedBrand}
                          onSelect={handleBrandSelect}
                          placeholder="Escribe para buscar marcas..."
                          disabled={false}
                          emptyMessage="No se encontraron marcas"
                        />
                        {errors.marca && (
                          <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '4px', fontWeight: 'bold' }}>
                            {errors.marca}
                          </div>
                        )}
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                          Descripción:
                        </label>
                        <textarea
                          rows={3}
                          value={productData.descripcion}
                          onChange={(e) => handleInputChange('descripcion', e.target.value)}
                          placeholder="Descripción del producto"
                          style={{ width: '100%', padding: '8px', border: `1px solid ${errors.descripcion ? '#dc3545' : '#ccc'}`, borderRadius: '4px', resize: 'vertical' }}
                        />
                        {errors.descripcion && (
                          <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '4px', fontWeight: 'bold' }}>
                            {errors.descripcion}
                          </div>
                        )}
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                          Composición:
                        </label>
                        <input
                          type="text"
                          value={productData.comp}
                          onChange={(e) => handleInputChange('comp', e.target.value)}
                          placeholder="Composición"
                          style={{ width: '100%', padding: '8px', border: `1px solid ${errors.comp ? '#dc3545' : '#ccc'}`, borderRadius: '4px' }}
                        />
                        {errors.comp && (
                          <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '4px', fontWeight: 'bold' }}>
                            {errors.comp}
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                        <div style={{ flex: '1' }}>
                          <label style={{ display: 'block', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                            Ancho:
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={productData.ancho}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || (value.indexOf('-') === -1 && parseFloat(value) >= 0)) {
                                handleInputChange('ancho', value);
                              }
                            }}
                            placeholder="cm"
                            style={{ width: '100%', padding: '8px', border: `1px solid ${errors.ancho ? '#dc3545' : '#ccc'}`, borderRadius: '4px' }}
                          />
                          {errors.ancho && (
                            <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '4px', fontWeight: 'bold' }}>
                              {errors.ancho}
                            </div>
                          )}
                        </div>
                        <div style={{ flex: '1' }}>
                          <label style={{ display: 'block', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                            Largo:
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={productData.largo}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || (value.indexOf('-') === -1 && parseFloat(value) >= 0)) {
                                handleInputChange('largo', value);
                              }
                            }}
                            placeholder="cm"
                            style={{ width: '100%', padding: '8px', border: `1px solid ${errors.largo ? '#dc3545' : '#ccc'}`, borderRadius: '4px' }}
                          />
                          {errors.largo && (
                            <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '4px', fontWeight: 'bold' }}>
                              {errors.largo}
                            </div>
                          )}
                        </div>
                        <div style={{ flex: '1' }}>
                          <label style={{ display: 'block', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                            Alto:
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={productData.alto}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || (value.indexOf('-') === -1 && parseFloat(value) >= 0)) {
                                handleInputChange('alto', value);
                              }
                            }}
                            placeholder="cm"
                            style={{ width: '100%', padding: '8px', border: `1px solid ${errors.alto ? '#dc3545' : '#ccc'}`, borderRadius: '4px' }}
                          />
                          {errors.alto && (
                            <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '4px', fontWeight: 'bold' }}>
                              {errors.alto}
                            </div>
                          )}
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                        <div style={{ flex: '1' }}>
                          <label style={{ display: 'block', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                            Ancho Paq:
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={productData.anchoPaq}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || (value.indexOf('-') === -1 && parseFloat(value) >= 0)) {
                                // Si es producto único, calcular volumen con valores actualizados
                                if (isUniqueProduct) {
                                  setProductData(prev => {
                                    const updatedData = { ...prev, anchoPaq: value };
                                    const { ancho, largo, alto } = calculateVolumeFromUpdatedData('anchoPaq', value, prev);
                                    const volumen = ancho * largo * alto;
                                    if (ancho > 0 && largo > 0 && alto > 0) {
                                      setUniqueVariant(v => ({ ...v, volumen: volumen.toFixed(2) }));
                                    } else {
                                      setUniqueVariant(v => ({ ...v, volumen: '' }));
                                    }
                                    return updatedData;
                                  });
                                  setErrors(prev => ({ ...prev, anchoPaq: undefined }));
                                } else {
                                  handleInputChange('anchoPaq', value);
                                }
                              }
                            }}
                            placeholder="cm"
                            style={{ width: '100%', padding: '8px', border: `1px solid ${errors.anchoPaq ? '#dc3545' : '#ccc'}`, borderRadius: '4px' }}
                          />
                          {errors.anchoPaq && (
                            <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '4px', fontWeight: 'bold' }}>
                              {errors.anchoPaq}
                            </div>
                          )}
                        </div>
                        <div style={{ flex: '1' }}>
                          <label style={{ display: 'block', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                            Largo Paq:
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={productData.largoPaq}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || (value.indexOf('-') === -1 && parseFloat(value) >= 0)) {
                                // Si es producto único, calcular volumen con valores actualizados
                                if (isUniqueProduct) {
                                  setProductData(prev => {
                                    const updatedData = { ...prev, largoPaq: value };
                                    const { ancho, largo, alto } = calculateVolumeFromUpdatedData('largoPaq', value, prev);
                                    const volumen = ancho * largo * alto;
                                    if (ancho > 0 && largo > 0 && alto > 0) {
                                      setUniqueVariant(v => ({ ...v, volumen: volumen.toFixed(2) }));
                                    } else {
                                      setUniqueVariant(v => ({ ...v, volumen: '' }));
                                    }
                                    return updatedData;
                                  });
                                  setErrors(prev => ({ ...prev, largoPaq: undefined }));
                                } else {
                                  handleInputChange('largoPaq', value);
                                }
                              }
                            }}
                            placeholder="cm"
                            style={{ width: '100%', padding: '8px', border: `1px solid ${errors.largoPaq ? '#dc3545' : '#ccc'}`, borderRadius: '4px' }}
                          />
                          {errors.largoPaq && (
                            <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '4px', fontWeight: 'bold' }}>
                              {errors.largoPaq}
                            </div>
                          )}
                        </div>
                        <div style={{ flex: '1' }}>
                          <label style={{ display: 'block', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                            Alto Paq:
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={productData.altoPaq}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || (value.indexOf('-') === -1 && parseFloat(value) >= 0)) {
                                // Si es producto único, calcular volumen con valores actualizados
                                if (isUniqueProduct) {
                                  setProductData(prev => {
                                    const updatedData = { ...prev, altoPaq: value };
                                    const { ancho, largo, alto } = calculateVolumeFromUpdatedData('altoPaq', value, prev);
                                    const volumen = ancho * largo * alto;
                                    if (ancho > 0 && largo > 0 && alto > 0) {
                                      setUniqueVariant(v => ({ ...v, volumen: volumen.toFixed(2) }));
                                    } else {
                                      setUniqueVariant(v => ({ ...v, volumen: '' }));
                                    }
                                    return updatedData;
                                  });
                                  setErrors(prev => ({ ...prev, altoPaq: undefined }));
                                } else {
                                  handleInputChange('altoPaq', value);
                                }
                              }
                            }}
                            placeholder="cm"
                            style={{ width: '100%', padding: '8px', border: `1px solid ${errors.altoPaq ? '#dc3545' : '#ccc'}`, borderRadius: '4px' }}
                          />
                          {errors.altoPaq && (
                            <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '4px', fontWeight: 'bold' }}>
                              {errors.altoPaq}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Columna Derecha */}
                    <div style={{ flex: '1', minWidth: '250px' }}>

                      <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                        <div style={{ flex: '1' }}>
                          <label style={{ display: 'block', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                            Categoría:
                          </label>
                          {loadingCategories ? (
                            <div style={{
                              padding: '12px',
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              borderRadius: '4px',
                              textAlign: 'center',
                              color: '#6c757d'
                            }}>
                              Cargando categorías...
                            </div>
                          ) : (
                            <>
                              <CategorySelectSearch
                                key={`category-${formKey}`}
                                options={odooCategories.map(category => ({
                                  id: category.odooId,
                                  value: category.display_name
                                }))}
                                placeholder="Seleccione categoría"
                                selected={productData.categoria}
                                onSelect={handleCategorySelect}
                                disabled={loadingCategories}
                                emptyMessage="No hay categorías disponibles"
                              />
                              {errors.categoria && (
                                <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '4px', fontWeight: 'bold' }}>
                                  {errors.categoria}
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        <div style={{ flex: '1' }}>
                          <label style={{ display: 'block', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                            Categorías Web: <span style={{ color: '#dc3545' }}>*</span>
                          </label>
                          <MultiCategoryWebSelect
                            key={`category-web-${formKey}`}
                            selectedCategories={selectedWebCategories}
                            onSelect={handleCategoryWebSelect}
                            onRemove={handleCategoryWebRemove}
                            placeholder="Escribe para buscar categorías web..."
                            disabled={false}
                            maxCategories={5}
                          />
                          <div style={{
                            fontSize: '11px',
                            color: '#666',
                            marginTop: '4px',
                            fontStyle: 'italic'
                          }}>
                            💡 Selecciona entre 1 y 5 categorías web (mínimo 1 requerida)
                          </div>
                          {errors.categoriaWeb && (
                            <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '4px', fontWeight: 'bold' }}>
                              {errors.categoriaWeb}
                            </div>
                          )}
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                        <div style={{ flex: '1' }}>
                          <label style={{ display: 'block', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                            Unidad:
                          </label>
                          <select
                            key={`unit-${formKey}`}
                            value={productData.unidad}
                            onChange={(e) => handleInputChange('unidad', e.target.value)}
                            style={{ width: '100%', padding: '8px', border: `1px solid ${errors.unidad ? '#dc3545' : '#ccc'}`, borderRadius: '4px' }}
                          >
                            <option value="">Seleccione unidad</option>
                            <option value="1">Unidad</option>
                            <option value="2">Kilogramos</option>
                            <option value="3">Litros</option>
                            <option value="4">Metros</option>
                          </select>
                          {errors.unidad && (
                            <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '4px', fontWeight: 'bold' }}>
                              {errors.unidad}
                            </div>
                          )}
                        </div>

                        <div style={{ flex: '1' }}>
                          <label style={{ display: 'block', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                            País:
                          </label>
                          <CategorySelectSearch
                            key={`country-${formKey}`}
                            options={countriesData}
                            placeholder="Seleccione país"
                            selected={productData.pais}
                            onSelect={handleCountrySelect}
                            disabled={false}
                            emptyMessage="No hay países disponibles"
                          />
                          {errors.pais && (
                            <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '4px', fontWeight: 'bold' }}>
                              {errors.pais}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Nuevos campos: Bajo Pedido y Fecha de Vencimiento */}
                      <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                        <div style={{ flex: '1', display: 'flex', alignItems: 'center', marginTop: '25px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 'bold', color: '#333' }}>
                            <input
                              type="checkbox"
                              checked={productData.bajoPedido === 'delivery'}
                              onChange={(e) => handleInputChange('bajoPedido', e.target.checked ? 'delivery' : 'order')}
                              style={{ marginRight: '10px', width: '20px', height: '20px' }}
                            />
                            Bajo pedido ?
                          </label>
                        </div>

                        <div style={{ flex: '1', display: 'flex', alignItems: 'center', marginTop: '25px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 'bold', color: '#333' }}>
                            <input
                              type="checkbox"
                              checked={productData.tieneVencimiento}
                              onChange={(e) => handleInputChange('tieneVencimiento', e.target.checked)}
                              style={{ marginRight: '10px', width: '20px', height: '20px' }}
                            />
                            Tiene fecha de vencimiento
                          </label>
                        </div>
                      </div>

                      {/* Sección de Imágenes del Producto */}
                      <div style={{
                        backgroundColor: '#f8f9fa',
                        border: '2px solid #e9ecef',
                        borderRadius: '12px',
                        padding: '20px',
                        marginBottom: '20px'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '15px'
                        }}>
                          <span style={{
                            backgroundColor: '#007bff',
                            color: 'white',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '10px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            📷
                          </span>
                          <h6 style={{ margin: '0', fontWeight: 'bold', color: '#333', fontSize: '16px' }}>
                            Imágenes del Producto
                          </h6>
                        </div>

                        {/* Imagen Principal */}
                        <div style={{ marginBottom: '20px' }}>
                          <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '10px', color: '#333' }}>
                            Imagen Principal: <span style={{ color: '#dc3545' }}>*</span>
                          </label>
                          <div
                            style={{
                              border: `2px dashed ${errors.imagePreview ? '#dc3545' : '#007bff'}`,
                              borderRadius: '12px',
                              padding: '25px',
                              marginBottom: '10px',
                              minHeight: '150px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'white',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              boxShadow: '0 2px 8px rgba(0, 123, 255, 0.1)'
                            }}
                            onClick={() => document.getElementById('imageUpload').click()}
                            onMouseEnter={(e) => e.target.style.borderColor = '#0056b3'}
                            onMouseLeave={(e) => e.target.style.borderColor = '#007bff'}
                          >
                            {imagePreview ? (
                              <img
                                src={imagePreview}
                                alt="Preview"
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '120px',
                                  objectFit: 'contain',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                }}
                              />
                            ) : (
                              <div style={{ textAlign: 'center', color: '#007bff' }}>
                                <div style={{ fontSize: '3em', marginBottom: '10px' }}>📷</div>
                                <p style={{ margin: '0', fontWeight: 'bold' }}>Click para subir imagen principal</p>
                                <p style={{ margin: '5px 0 0 0', fontSize: '12px', opacity: '0.7' }}>
                                  Imagen cuadrada, máximo 1500x1500px, 1MB
                                </p>
                              </div>
                            )}
                            {errors.imagePreview && (
                              <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '4px', fontWeight: 'bold' }}>
                                {errors.imagePreview}
                              </div>
                            )}
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            style={{ display: 'none' }}
                            id="imageUpload"
                          />
                        </div>

                        {/* Checkbox para imágenes adicionales */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          marginBottom: '15px'
                        }}>
                          <input
                            type="checkbox"
                            id="showAdditionalImages"
                            checked={showAdditionalImages}
                            onChange={(e) => {
                              setShowAdditionalImages(e.target.checked);
                              // Limpiar errores de imágenes adicionales cuando se deselecciona
                              if (!e.target.checked) {
                                setErrors(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors.additionalImages;
                                  return newErrors;
                                });
                              }
                            }}
                            style={{
                              width: '18px',
                              height: '18px',
                              cursor: 'pointer'
                            }}
                          />
                          <label
                            htmlFor="showAdditionalImages"
                            style={{
                              fontSize: '14px',
                              fontWeight: 'bold',
                              color: '#333',
                              cursor: 'pointer',
                              margin: '0'
                            }}
                          >
                            ¿Quieres agregar imágenes adicionales?
                          </label>
                        </div>
                        {showAdditionalImages && (
                          <p style={{ margin: '0 0 15px 0', color: '#666', fontSize: '12px', fontStyle: 'italic' }}>
                            Los campos marcados con <span style={{ color: '#dc3545' }}>*</span> son obligatorios cuando se activa esta opción.
                          </p>
                        )}

                        {/* Imágenes Adicionales */}
                        {showAdditionalImages && (
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                              <label style={{ fontSize: '14px', fontWeight: 'bold', color: '#333' }}>
                                Imágenes Adicionales: <span style={{ color: '#dc3545' }}>*</span>
                              </label>
                              <span style={{
                                fontSize: '12px',
                                color: additionalImages.length >= 5 ? '#dc3545' : '#28a745',
                                fontWeight: 'bold'
                              }}>
                                {additionalImages.length}/5
                              </span>
                            </div>

                            <div style={{
                              padding: '8px 12px',
                              backgroundColor: '#fff3cd',
                              border: '1px solid #ffeaa7',
                              borderRadius: '6px',
                              fontSize: '11px',
                              color: '#856404',
                              marginBottom: '10px'
                            }}>
                              <strong>📋 Requisitos:</strong> Imágenes cuadradas, máximo 1500x1500px, 1MB cada una. Máximo 5 imágenes.
                            </div>

                            {additionalImages.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
                                {additionalImages.map((image, index) => (
                                  <div key={index} style={{ position: 'relative', width: '80px', height: '80px', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                    <img src={image} alt={`Additional ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        removeAdditionalImage(index);
                                      }}
                                      style={{
                                        position: 'absolute',
                                        top: '2px',
                                        right: '2px',
                                        background: 'rgba(255, 255, 255, 0.9)',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '20px',
                                        height: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        color: '#dc3545',
                                        fontWeight: 'bold',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                      }}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            {additionalImages.length < 5 && (
                              <div
                                style={{
                                  border: `2px dashed ${errors.additionalImages ? '#dc3545' : '#007bff'}`,
                                  borderRadius: '8px',
                                  padding: '20px',
                                  minHeight: '80px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginBottom: '5px',
                                  cursor: 'pointer',
                                  backgroundColor: 'white',
                                  transition: 'all 0.3s ease'
                                }}
                                onClick={() => document.getElementById('additionalImageUpload').click()}
                                onMouseEnter={(e) => e.target.style.borderColor = '#0056b3'}
                                onMouseLeave={(e) => e.target.style.borderColor = '#007bff'}
                              >
                                <div style={{ textAlign: 'center' }}>
                                  <span style={{ fontSize: '24px', color: '#007bff' }}>📷</span>
                                  <div style={{ fontSize: '12px', color: '#007bff', marginTop: '5px', fontWeight: 'bold' }}>
                                    Click para subir imágenes adicionales
                                  </div>
                                  <div style={{ fontSize: '10px', color: '#666', marginTop: '3px' }}>
                                    {5 - additionalImages.length} espacios disponibles
                                  </div>
                                </div>
                              </div>
                            )}

                            {additionalImages.length >= 5 && (
                              <div style={{
                                padding: '12px',
                                backgroundColor: '#f8d7da',
                                border: '1px solid #f5c6cb',
                                borderRadius: '6px',
                                fontSize: '12px',
                                color: '#721c24',
                                textAlign: 'center'
                              }}>
                                <strong>⚠️ Límite alcanzado:</strong> Ya tienes 5 imágenes adicionales
                              </div>
                            )}

                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleAdditionalImagesUpload}
                              style={{ display: 'none' }}
                              id="additionalImageUpload"
                              multiple
                            />
                            {errors.additionalImages && (
                              <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '4px', fontWeight: 'bold' }}>
                                {errors.additionalImages}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Sección de Variantes */}
                  {/* NUEVO: Checkbox para producto único */}
                  <div style={{ margin: '30px 0 20px 0', padding: '18px', background: '#e9f7ef', borderRadius: '12px', border: '1.5px solid #b2f2e5', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <input
                      type="checkbox"
                      id="uniqueProductCheckbox"
                      checked={isUniqueProduct}
                      onChange={e => {
                        setIsUniqueProduct(e.target.checked);
                        // Limpiar errores de variante única cuando se deselecciona
                        if (!e.target.checked) {
                          setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.uniqueVariant;
                            return newErrors;
                          });
                        }
                      }}
                      style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                    />
                    <label htmlFor="uniqueProductCheckbox" style={{ fontWeight: 600, fontSize: 17, color: '#000000', cursor: 'pointer', margin: 0 }}>
                      ¿Desea cargar este producto como único (sin variantes ni atributos)?
                    </label>
                  </div>

                  {/* Si es producto único, mostrar campos simples */}
                  {isUniqueProduct ? (
                    <div style={{ background: '#f4f2fb', border: '2px solid #dcdeff', borderRadius: '16px', padding: '25px', marginTop: '10px', boxShadow: '#0076f65c 0px 8px 32px' }}>
                      <h4 style={{ margin: '0 0 20px 0', fontWeight: '700', color: '#333', fontSize: '20px' }}>
                        Datos del Producto Único
                      </h4>
                      <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '14px', fontStyle: 'italic' }}>
                        Los campos marcados con <span style={{ color: '#dc3545' }}>*</span> son obligatorios para productos únicos.
                      </p>
                      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '200px' }}>
                          <label style={{ fontWeight: 'bold', color: '#333', marginBottom: '5px', display: 'block' }}>SKU: <span style={{ color: '#dc3545' }}>*</span></label>
                          <input
                            type="text"
                            value={uniqueVariant.sku}
                            onChange={e => setUniqueVariant(v => ({ ...v, sku: e.target.value }))}
                            placeholder="SKU único"
                            style={{ width: '100%', padding: '8px', border: `1px solid ${errors.uniqueVariant?.sku ? '#dc3545' : '#ccc'}`, borderRadius: '4px' }}
                          />
                          {errors.uniqueVariant?.sku && (
                            <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '4px', fontWeight: 'bold' }}>
                              {errors.uniqueVariant.sku}
                            </div>
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: '120px' }}>
                          <label style={{ fontWeight: 'bold', color: '#333', marginBottom: '5px', display: 'block' }}>Peso (kg): <span style={{ color: '#dc3545' }}>*</span></label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={uniqueVariant.peso}
                            onChange={e => {
                              const value = e.target.value;
                              if (value === '' || (value.indexOf('-') === -1 && parseFloat(value) >= 0)) {
                                setUniqueVariant(v => ({ ...v, peso: value }));
                              }
                            }}
                            placeholder="Peso en kg"
                            style={{ width: '100%', padding: '8px', border: `1px solid ${errors.uniqueVariant?.peso ? '#dc3545' : '#ccc'}`, borderRadius: '4px' }}
                          />
                          {errors.uniqueVariant?.peso && (
                            <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '4px', fontWeight: 'bold' }}>
                              {errors.uniqueVariant.peso}
                            </div>
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: '120px' }}>
                          <label style={{ fontWeight: 'bold', color: '#333', marginBottom: '5px', display: 'block' }}>
                            Volumen (cm<sup>3</sup>): <span style={{ color: '#dc3545' }}>*</span>
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={uniqueVariant.volumen}
                            onChange={e => {
                              const value = e.target.value;
                              if (value === '' || (value.indexOf('-') === -1 && parseFloat(value) >= 0)) {
                                setUniqueVariant(v => ({ ...v, volumen: value }));
                              }
                            }}
                            placeholder="Volumen"
                            style={{ width: '100%', padding: '8px', border: `1px solid ${errors.uniqueVariant?.volumen ? '#dc3545' : '#ccc'}`, borderRadius: '4px' }}
                          />
                          {errors.uniqueVariant?.volumen && (
                            <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '4px', fontWeight: 'bold' }}>
                              {errors.uniqueVariant.volumen}
                            </div>
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: '120px' }}>
                          <label style={{ fontWeight: 'bold', color: '#333', marginBottom: '5px', display: 'block' }}>Código de barras: <span style={{ color: '#dc3545' }}>*</span></label>
                          <input
                            type="text"
                            value={uniqueVariant.barcode}
                            onChange={e => {
                              const value = e.target.value;
                              // Solo permitir números
                              if (value === '' || /^\d+$/.test(value)) {
                                setUniqueVariant(v => ({ ...v, barcode: value }));
                              }
                            }}
                            placeholder="Solo números (mín. 8 dígitos)"
                            minLength="8"
                            maxLength="13"
                            style={{ width: '100%', padding: '8px', border: `1px solid ${errors.uniqueVariant?.barcode ? '#dc3545' : '#ccc'}`, borderRadius: '4px' }}
                          />
                          {errors.uniqueVariant?.barcode && (
                            <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '4px', fontWeight: 'bold' }}>
                              {errors.uniqueVariant.barcode}
                            </div>
                          )}
                          {uniqueVariant.barcode && uniqueVariant.barcode.length < 8 && (
                            <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px', fontWeight: 'bold' }}>
                              ⚠️ El código de barras debe tener al menos 8 dígitos
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      backgroundColor: '#f4f2fb',
                      border: '2px solid #dcdeff',
                      borderRadius: '16px',
                      padding: '25px',
                      marginTop: '30px',
                      boxShadow: '#0076f65c 0px 8px 32px'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '20px',
                        paddingBottom: '15px',
                        borderBottom: '2px solid #fed7d7'
                      }}>

                        <h4 style={{ margin: '0', fontWeight: '700', color: '#333', fontSize: '20px' }}>
                          Variantes del Producto
                        </h4>
                      </div>

                      <div style={{ textAlign: 'center', marginBottom: '25px' }}>
                        <button
                          type="button"
                          onClick={addVariant}
                          style={{
                            padding: '10px 40px',
                            background: 'linear-gradient(135deg, rgb(0, 123, 255) 0%, rgb(0, 86, 179) 100%)',
                            boxShadow: '0 6px 20px rgba(0, 123, 255, 0.3)',
                            border: 'none',
                            color: 'white',
                            borderRadius: '13px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            margin: '10px auto 0',
                            gap: '10px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 8px 25px rgba(0, 123, 255, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 6px 20px rgba(0, 123, 255, 0.3)';
                          }}
                        >
                          Agregar Variantes
                        </button>
                        <p style={{ margin: '10px 0 0 0', fontSize: '14px', color: '#666', fontWeight: '500' }}>
                          Agrega variantes a tu producto
                        </p>
                      </div>

                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginBottom: '20px',
                        padding: '15px',
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        border: '1px solid #fed7d7'
                      }}>
                        <span style={{
                          fontSize: '18px',
                          marginRight: '10px',
                          color: '#dc3545'
                        }}>
                          📦
                        </span>
                        <h6 style={{ margin: '0', fontWeight: 'bold', color: '#0076f4', fontSize: '16px' }}>
                          Variantes agregadas ({modalVariants.filter(v => v && (v.sku || v.odooAttributes)).length})
                        </h6>
                      </div>

                      {/* Mostrar variantes del modal */}
                      {modalVariants.filter(v => v && (v.sku || v.odooAttributes)).length === 0 ? (
                        <div style={{
                          textAlign: 'center',
                          padding: '40px 20px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px',
                          border: '2px dashed #dee2e6'
                        }}>
                          <div style={{ fontSize: '48px', marginBottom: '15px' }}>📦</div>
                          <h6 style={{ margin: '0 0 10px 0', color: '#6c757d' }}>No hay variantes agregadas</h6>
                          <p style={{ margin: '0', color: '#6c757d', fontSize: '14px' }}>
                            Haz clic en "Agregar Variante" para comenzar a crear variantes con atributos de Odoo
                          </p>
                        </div>
                      ) : (
                        modalVariants
                          .filter(variant => variant && (variant.sku || variant.odooAttributes))
                          .map((variant, index) => (
                            <div key={index} style={{
                              marginBottom: '20px',
                              padding: '20px',
                              backgroundColor: 'white',
                              borderRadius: '8px',
                              border: '1px solid #e9ecef',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                            }}>
                              {/* Header de la variante */}
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '15px',
                                paddingBottom: '10px',
                                borderBottom: '1px solid #e9ecef'
                              }}>
                                <h6 style={{ margin: '0', fontWeight: 'bold', color: '#495057' }}>
                                  Variante {index + 1}
                                </h6>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    removeModalVariant(index);
                                  }}
                                  style={{
                                    padding: '4px 8px',
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                  }}
                                >
                                  ✕ Eliminar
                                </button>
                              </div>

                              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                                {/* Atributos de Odoo */}
                                {variant.odooAttributes && variant.odooAttributes.length > 0 && (
                                  <div style={{ flex: '1', minWidth: '300px' }}>
                                    <h6 style={{ margin: '0 0 15px 0', fontWeight: 'bold', color: '#28a745', fontSize: '14px' }}>
                                      Atributos de la variante:
                                    </h6>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                      {variant.odooAttributes.map((attr, attrIndex) => (
                                        <span key={attrIndex} style={{
                                          padding: '8px 16px',
                                          backgroundColor: '#f8f9fa',
                                          color: '#495057',
                                          borderRadius: '25px',
                                          fontSize: '13px',
                                          fontWeight: '600',
                                          border: '1px solid #e9ecef',
                                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                        }}>
                                          <span style={{ color: '#28a745', fontWeight: 'bold' }}>{attr.attributeName}:</span> {attr.valueName}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Detalles de la variante */}
                                <div style={{ flex: '1', minWidth: '300px' }}>
                                  <h6 style={{ margin: '0 0 15px 0', fontWeight: 'bold', color: '#28a745', fontSize: '14px' }}>
                                    Detalles:
                                  </h6>
                                  <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '12px'
                                  }}>
                                    {variant.peso && (
                                      <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        minWidth: '80px'
                                      }}>
                                        <div style={{
                                          fontSize: '10px',
                                          fontWeight: 'bold',
                                          color: '#6c757d',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.5px',
                                          marginBottom: '6px'
                                        }}>
                                          Peso (kg)
                                        </div>
                                        <div style={{
                                          fontSize: '15px',
                                          color: '#495057',
                                          fontWeight: 'bold',
                                          padding: '6px 12px',
                                          borderRadius: '20px',
                                          backgroundColor: '#e8f5e8',
                                          border: '1px solid #d4edda',
                                          color: '#155724'
                                        }}>
                                          {variant.peso}
                                        </div>
                                      </div>
                                    )}
                                    {variant.volumen && (
                                      <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        minWidth: '80px'
                                      }}>
                                        <div style={{
                                          fontSize: '10px',
                                          fontWeight: 'bold',
                                          color: '#6c757d',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.5px',
                                          marginBottom: '6px'
                                        }}>
                                          Volumen (cm<sup>3</sup>)
                                        </div>
                                        <div style={{
                                          fontSize: '15px',
                                          color: '#495057',
                                          fontWeight: 'bold',
                                          padding: '6px 12px',
                                          borderRadius: '20px',
                                          backgroundColor: '#e8f5e8',
                                          border: '1px solid #d4edda',
                                          color: '#155724'
                                        }}>
                                          {variant.volumen}
                                        </div>
                                      </div>
                                    )}
                                    {variant.sku && (
                                      <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        minWidth: '100px'
                                      }}>
                                        <div style={{
                                          fontSize: '10px',
                                          fontWeight: 'bold',
                                          color: '#6c757d',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.5px',
                                          marginBottom: '6px'
                                        }}>
                                          SKU
                                        </div>
                                        <div style={{
                                          fontSize: '13px',
                                          fontWeight: 'bold',
                                          padding: '6px 12px',
                                          borderRadius: '20px',
                                          backgroundColor: '#fff3cd',
                                          border: '1px solid #ffeaa7',
                                          color: '#856404',
                                          fontFamily: 'monospace'
                                        }}>
                                          {variant.sku}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Imágenes */}
                                <div style={{ flex: '1', minWidth: '200px' }}>
                                  <h6 style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#28a745' }}>
                                    Imágenes:
                                  </h6>
                                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                    {variant.image && (
                                      <div style={{ textAlign: 'center' }}>
                                        <img
                                          src={variant.image}
                                          alt="Imagen principal"
                                          style={{
                                            width: '60px',
                                            height: '60px',
                                            objectFit: 'cover',
                                            borderRadius: '4px',
                                            border: '2px solid #007bff'
                                          }}
                                        />
                                        <div style={{ fontSize: '10px', color: '#6c757d', marginTop: '2px' }}>Principal</div>
                                      </div>
                                    )}
                                    {variant.additionalImages && variant.additionalImages.length > 0 && (
                                      variant.additionalImages.map((img, imgIndex) => (
                                        <div key={imgIndex} style={{ textAlign: 'center' }}>
                                          <img
                                            src={img}
                                            alt={`Adicional ${imgIndex + 1}`}
                                            style={{
                                              width: '50px',
                                              height: '50px',
                                              objectFit: 'cover',
                                              borderRadius: '4px',
                                              border: '1px solid #dee2e6'
                                            }}
                                          />
                                          <div style={{ fontSize: '10px', color: '#6c757d', marginTop: '2px' }}>+{imgIndex + 1}</div>
                                        </div>
                                      ))
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                      )}
                    </div>
                  )}

                  {/* Botón de envío - Solo se muestra si hay variantes válidas o si es producto único */}
                  {((modalVariants.length > 0 && modalVariants.some(v => v && (v.sku || v.odooAttributes))) || isUniqueProduct) && (
                    <div style={{ textAlign: 'center', marginTop: '30px' }}>
                      <button
                        type="submit"
                        style={{
                          padding: '15px 40px',
                          background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '25px',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          boxShadow: '0 6px 20px rgba(40, 167, 69, 0.3)',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 8px 25px rgba(40, 167, 69, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 6px 20px rgba(40, 167, 69, 0.3)';
                        }}
                      >
                        🚀 Enviar Producto
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>

          {/* Panel lateral derecho - Atributos */}
          {/* <div style={{ flex: '1', minWidth: '300px' }}>
            <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
              <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderBottom: '1px solid #dee2e6' }}>
                <h6 style={{ margin: '0', fontWeight: 'bold' }}>Atributos</h6>
              </div>
              
              <div style={{ padding: '20px' }}>
                <div style={{ marginBottom: '15px' }}>
                  <span style={{ 
                    display: 'inline-block', 
                    padding: '4px 8px', 
                    backgroundColor: '#6c757d', 
                    color: 'white', 
                    borderRadius: '4px', 
                    marginRight: '8px',
                    marginBottom: '8px'
                  }}>
                    Color
                  </span>
                  <span style={{ 
                    display: 'inline-block', 
                    padding: '4px 8px', 
                    backgroundColor: '#6c757d', 
                    color: 'white', 
                    borderRadius: '4px', 
                    marginRight: '8px',
                    marginBottom: '8px'
                  }}>
                    Talla
                  </span>
                </div>
                
                <div style={{ border: '1px solid #dee2e6', borderRadius: '4px', padding: '15px', backgroundColor: '#f8f9fa' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' }}>
                        Color:
                      </label>
                      <input 
                        style={{ width: '100%', padding: '4px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '2px' }}
                        placeholder="Color" 
                        disabled 
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' }}>
                        Talla:
                      </label>
                      <input 
                        style={{ width: '100%', padding: '4px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '2px' }}
                        placeholder="Talla" 
                        disabled 
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' }}>
                        Peso:
                      </label>
                      <input 
                        style={{ width: '100%', padding: '4px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '2px' }}
                        placeholder="Peso" 
                        disabled 
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' }}>
                        Volumen:
                      </label>
                      <input 
                        style={{ width: '100%', padding: '4px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '2px' }}
                        placeholder="Volumen" 
                        disabled 
                      />
                    </div>
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' }}>
                      SKU:
                    </label>
                    <input 
                      style={{ width: '100%', padding: '4px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '2px' }}
                      placeholder="SKU" 
                      disabled 
                    />
                  </div>
                  
                  <div style={{ textAlign: 'center' }}>
                    <div 
                      style={{ 
                        border: '1px dashed #ccc', 
                        borderRadius: '4px', 
                        padding: '15px',
                        minHeight: '60px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        marginBottom: '5px'
                      }}
                    >
                      <span style={{ fontSize: '20px' }}>📷</span>
                    </div>
                    <small style={{ color: '#666' }}>Imagen variante</small>
                  </div>
                </div>

                <div style={{ 
                  marginTop: '15px', 
                  padding: '10px', 
                  backgroundColor: '#d1ecf1', 
                  border: '1px solid #bee5eb', 
                  borderRadius: '4px',
                  fontSize: '14px'
                }}>
                  <strong>Nota:</strong> Las variantes se generarán automáticamente basadas en los colores y tallas especificados.
                </div>
              </div>
            </div>
          </div> */}
        </div>
      </div>

      {/* Modal de Variante */}
      {showVariantModal && (
        <VariantModal
          isOpen={showVariantModal}
          onClose={closeVariantModal}
          onSave={saveVariants}
          variantData={variantData}
          onVariantDataChange={handleVariantDataChange}
          variantImagePreview={variantImagePreview}
          onImageUpload={handleVariantImageUpload}
          removeVariant={removeModalVariant}
          variants={modalVariants}
          existingVariants={modalVariants} // Pasar las variantes existentes
          packageDimensions={{
            anchoPaq: productData.anchoPaq,
            largoPaq: productData.largoPaq,
            altoPaq: productData.altoPaq
          }}
        />
      )}

      {/* Modal de notificación de envío */}
      <SubmitNotificationModal
        open={notification.open}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        autoCloseMs={notification.type === 'success' ? 5000 : 0}
      />
    </div>
  );
};

export default ProductManagementClient; 